import * as THREE from 'three';
import type { MudDrop } from '@/sim/SimCore';

/**
 * MudView.ts — the SLIPPERY MUD item's visual (M59): a dark, glistening puddle
 * disc on the road where the item was poured. Purely cosmetic — SimCore owns
 * the puddle list + physics (grip loss + speed bleed for rivals crossing it);
 * this view mirrors it each frame, tinting each puddle a muddy brown flecked
 * with the firer's colour and fading it out over its last second. Pooled: one
 * reusable disc per possible puddle.
 *
 * A shared radial "wet splat" texture (soft dark centre, ragged edge, a couple
 * of highlight glints) gives it a slick, organic read instead of a flat coin.
 */

/** Muddy-puddle texture, built once and shared by every puddle disc. Rounded
 *  and wet: a soft-edged mud body (darkest at the centre), a gently irregular
 *  but ROUND rim that fades to transparent, glossy sheen streaks, and a few
 *  darker speckles + lighter flecks so it reads as textured mud, not a coin. */
function mudTexture(): THREE.CanvasTexture {
  const S = 256;
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  const cx = S / 2;
  const cy = S / 2;
  x.clearRect(0, 0, S, S);

  // A gently wavy ROUND rim (small sinusoidal wobble — organic, not jagged),
  // clipped so everything paints inside the puddle.
  x.beginPath();
  const R = S * 0.46;
  for (let i = 0; i <= 64; i++) {
    const a = (i / 64) * Math.PI * 2;
    const r = R * (0.93 + 0.07 * Math.sin(a * 5) + 0.03 * Math.sin(a * 9 + 1));
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    if (i === 0) x.moveTo(px, py);
    else x.lineTo(px, py);
  }
  x.closePath();
  x.save();
  x.clip();

  // Mud body — darkest in the middle, lightening toward the rim, alpha fading
  // out at the very edge so it blends onto the asphalt.
  const g = x.createRadialGradient(cx, cy, R * 0.1, cx, cy, R);
  g.addColorStop(0, 'rgba(40,28,16,0.98)');
  g.addColorStop(0.55, 'rgba(58,40,23,0.96)');
  g.addColorStop(0.85, 'rgba(70,50,30,0.9)');
  g.addColorStop(1, 'rgba(70,50,30,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, S, S);

  // Darker speckles + a few wet-mud lumps for texture.
  for (let i = 0; i < 60; i++) {
    const a = (i * 2.399) % (Math.PI * 2);
    const rr = R * (0.15 + ((i * 37) % 80) / 100);
    const px = cx + Math.cos(a) * rr;
    const py = cy + Math.sin(a) * rr;
    x.fillStyle = i % 3 === 0 ? 'rgba(28,18,10,0.5)' : 'rgba(96,72,44,0.4)';
    x.beginPath();
    x.arc(px, py, 2 + ((i * 13) % 5), 0, Math.PI * 2);
    x.fill();
  }

  // Glossy sheen — a couple of pale, soft highlight streaks (wet look).
  x.globalAlpha = 0.3;
  x.fillStyle = '#b7ad95';
  x.beginPath();
  x.ellipse(cx - R * 0.22, cy - R * 0.28, R * 0.32, R * 0.1, -0.5, 0, Math.PI * 2);
  x.fill();
  x.globalAlpha = 0.18;
  x.beginPath();
  x.ellipse(cx + R * 0.25, cy + R * 0.2, R * 0.18, R * 0.06, 0.4, 0, Math.PI * 2);
  x.fill();
  x.globalAlpha = 1;

  x.restore();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

let sharedMudTex: THREE.CanvasTexture | null = null;

/** One pooled puddle: a flat textured disc lying on the road. */
class MudVisual {
  readonly mesh: THREE.Mesh;
  readonly mat: THREE.MeshBasicMaterial;

  constructor() {
    sharedMudTex ??= mudTexture();
    this.mat = new THREE.MeshBasicMaterial({
      map: sharedMudTex,
      transparent: true,
      opacity: 0.95, // M60: more opaque — the owner missed seeing it
      depthWrite: false,
    });
    // Unit-radius ROUND disc, laid flat; scaled to the puddle radius per frame.
    const geo = new THREE.CircleGeometry(1, 40);
    geo.rotateX(-Math.PI / 2); // lie on the ground (normal +Y)
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.visible = false;
  }

  dispose(): void {
    this.mat.dispose();
    this.mesh.geometry.dispose();
  }
}

export class MudView {
  readonly group = new THREE.Group();
  private pool: MudVisual[] = [];

  /** Mirror the sim's mud list (called each render frame). */
  update(mud: MudDrop[]): void {
    while (this.pool.length < mud.length) {
      const v = new MudVisual();
      this.pool.push(v);
      this.group.add(v.mesh);
    }
    for (let i = 0; i < this.pool.length; i++) {
      const v = this.pool[i];
      const m = mud[i];
      if (!m) {
        v.mesh.visible = false;
        continue;
      }
      v.mesh.visible = true;
      // A hair above the road so it never z-fights the asphalt.
      v.mesh.position.set(m.x, m.y + 0.05, m.z);
      v.mesh.scale.set(m.radius, 1, m.radius);
      // Muddy brown flecked toward the firer's colour, faded over the last sec.
      v.mat.color.set(0x6a4a2c).lerp(new THREE.Color(m.color), 0.18);
      v.mat.opacity = 0.95 * Math.min(1, m.timer);
    }
  }

  hide(): void {
    for (const v of this.pool) v.mesh.visible = false;
  }

  dispose(): void {
    for (const v of this.pool) v.dispose();
    this.pool = [];
  }
}
